const appendToObjects = (obj, appends) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, { ...v, ...appends }]));

export const makeParamsOptional = (obj) => appendToObjects(obj, { optional: true });
export const makeOptional = (obj) => ({ ...obj, optional: true });
export const makeUnique = (obj) => ({ ...obj, unique: true });
export const removeDefault = (obj) => ({ ...obj, default: undefined });
export const addDefault = (obj, default_value) => ({ ...obj, default: default_value });

export const VALIDATOR_MODE = {
  quick: 'quick',
  precise: 'precise',
};

export const PATTERN = {
  numeric: /^\d+$/,
  alphanum: /^\w+$/,
  alpha_upper: /^[A-Z]+$/,
  alpha_lower: /^[a-z]+$/,
  password: /^(?=.*[A-Z].*[A-Z])(?=.*[!@#$&*])(?=.*[0-9].*[0-9])(?=.*[a-z].*[a-z].*[a-z])$/,
};

export const SCHEMA_TYPE = {
  object: 'object',
  string: 'string',
  array: 'array',
  number: 'number',
  url: 'url',
  email: 'email',
  enum: 'enum',
  equal: 'equal',
  uuid: 'uuid',
  boolean: 'boolean',
};

const getEnumFromArray = (arr, default_value) => ({
  type: SCHEMA_TYPE.enum,
  values: arr,
  default: default_value,
});

const getEnum = (obj, default_value) => getEnumFromArray(Object.values(obj), default_value);

const getEnumMultiple = (obj, opts) => {
  const defaults = { items: SCHEMA_TYPE.number, min: 1, max: 10, unique: true };
  return {
    ...defaults,
    ...opts,
    type: SCHEMA_TYPE.array,
    enum: Object.values(obj),
  };
};

const getFieldsFromArray = (arr, default_value = ['*']) => ({
  type: SCHEMA_TYPE.array,
  items: 'string',
  enum: arr,
  default: default_value,
});

export const objectWrapper = (props, opts) => ({
  strict: 'remove',
  ...opts,
  type: SCHEMA_TYPE.object,
  props,
});

export const arrayOfObjectsWrapper = (object_props, opts) => ({
  ...opts,
  type: SCHEMA_TYPE.array,
  items: objectWrapper(object_props),
});

export const getOrderByListBySchema = (fields) => {
  const opts = { optional: true };
  const schema = {
    field: {
      type: SCHEMA_TYPE.enum,
      values: fields,
    },
    order: {
      type: SCHEMA_TYPE.array,
      unique: true,
      min: 1,
      max: 10,
      items: { type: SCHEMA_TYPE.number, integer: true, min: 1, max: 10 },
    },
  };
  return objectWrapper(schema, opts);
};

export const getOrderBySchema = (fields, array = false) => {
  const opts = { optional: true };
  const schema = {
    field: {
      type: SCHEMA_TYPE.enum,
      values: fields,
    },
    order: {
      type: 'enum',
      values: ['ASC', 'DESC'],
    },
  };
  if (array === true) {
    return arrayOfObjectsWrapper(schema, opts);
  }
  return objectWrapper(schema, opts);
};

const getLimitSchema = values => ({
  type: SCHEMA_TYPE.enum,
  optional: true,
  values,
});

const getOffsetSchema = (multiplyOf, max = Number.MAX_VALUE) => ({
  type: SCHEMA_TYPE.number,
  integer: true,
  optional: true,
  min: 0,
  max,
  custom: (value, errors) => {
    if (value && value % multiplyOf !== 0) errors.push({ type: 'multiplyOf', expected: multiplyOf, actual: value });
    return value;
  },
});

const getCodeSchema = (length = 4) => ({
  type: SCHEMA_TYPE.string,
  pattern: PATTERN.numeric,
  min: length,
  max: length,
});

export const SCHEMA_GENERATOR = {
  getCodeSchema,
  getOffsetSchema,
  getLimitSchema,
  getOrderBySchema,
  getOrderByListBySchema,
  getEnumFromArray,
  getEnum,
  getEnumMultiple,
  getFieldsFromArray,
};

const BASIC_SCHEMA_FIELD = {
  int: { type: SCHEMA_TYPE.number, integer: true, min: 0, max: Number.MAX_VALUE },
  uint: { type: SCHEMA_TYPE.number, integer: true, min: 1, max: Number.MAX_VALUE },
  bigint: { type: SCHEMA_TYPE.string, pattern: PATTERN.numeric },
};

const MB = (mb) => mb * 1024 * 1024;

export const SCHEMA_FIELD = {
  ...BASIC_SCHEMA_FIELD,
  password: { type: SCHEMA_TYPE.string, min: 8, max: 64 },
  email: { type: SCHEMA_TYPE.email, min: 3, max: 64, mode: VALIDATOR_MODE.precise },
  address_line: { type: SCHEMA_TYPE.string, min: 1, max: 50, clear_empty: true },
  name: { type: SCHEMA_TYPE.string, min: 1, max: 64, clear_empty: true },
  kyc_input: {
    type: SCHEMA_TYPE.string,
    min: 1,
    max: 64,
    clear_empty: true,
    pattern: '^[A-Za-z0-9-/ ]+$',
    pattern_error: 'stringPatternLatin',
  },
  extension: { type: SCHEMA_TYPE.string, min: 3, max: 3, clear_empty: true },
  hash: { type: SCHEMA_TYPE.string, min: 64, max: 64 },
  bit: { type: SCHEMA_TYPE.number, min: 0, max: 1, integer: true },
  true: { type: SCHEMA_TYPE.equal, value: 1, strict: true },
  base64_image: { type: SCHEMA_TYPE.string, pattern: /data:image\/.*;base64,.*/, max: MB(10) },
  base64_pdf: { type: SCHEMA_TYPE.string, pattern: /data:application\/pdf;base64,.*/, max: MB(10) },
  base64_image_or_pdf: {
    type: SCHEMA_TYPE.string,
    pattern: /data:(application\/pdf|image\/.*);base64,.*/,
    max: MB(10),
  },
  id: BASIC_SCHEMA_FIELD.uint,
  int: BASIC_SCHEMA_FIELD.int,
  big_id: BASIC_SCHEMA_FIELD.bigint,
  phone_number: { type: SCHEMA_TYPE.string, pattern: PATTERN.numeric, min: 6, max: 14, integer: true },
  url: { type: SCHEMA_TYPE.url, min: 10, max: 1_000 },
  price_usd: { type: SCHEMA_TYPE.number, min: 0.01, max: 200_000.00, positive: true, amount: true },
  price_int: { type: SCHEMA_TYPE.number, min: 10, max: 200_000_000, positive: true, amount: true, integer: true },
  amount_int: { type: SCHEMA_TYPE.number, min: -200_000_000, max: 200_000_000, amount: true, integer: true },
  code: getCodeSchema(4),
  text: { type: SCHEMA_TYPE.string, min: 1, max: 20_480 },
  twofa_code: getCodeSchema(6),
  int_array: { type: SCHEMA_TYPE.array, min: 1, max: 100, items: BASIC_SCHEMA_FIELD.int },
  big_id_array: {
    type: SCHEMA_TYPE.array,
    min: 1,
    max: 100,
    items: { type: SCHEMA_TYPE.string, pattern: PATTERN.numeric },
  },
  string_array: { type: SCHEMA_TYPE.array, min: 1, max: 100, items: { type: SCHEMA_TYPE.string, min: 1, max: 64 } },
  uuid: { type: SCHEMA_TYPE.uuid },
  uuid_array: { type: SCHEMA_TYPE.array, min: 1, max: 100, items: { type: SCHEMA_TYPE.string } },
  date: { type: 'string', pattern: /^([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))$/ },
  date_hour: { type: 'string', pattern: /^([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))( (0[0-9]|1[0-9]|2[0-3])(:[0-5]\d){2})?$/ },
  year_month: { type: 'string', pattern: /^([12]\d{3}-(0[1-9]|1[0-2]))$/ },
  card_expiration: { type: 'string', pattern: /^((0[1-9]|1[0-2])\/[20]\d{3})$/ },
  ip: { type: 'string', min: 8, max: 46 },
  country: { type: SCHEMA_TYPE.string, min: 2, max: 2, pattern: PATTERN.alpha_upper },
  card_pan: { type: SCHEMA_TYPE.string, min: 15, max: 19, pattern: PATTERN.numeric },
  card_ccv: { type: SCHEMA_TYPE.string, min: 3, max: 4, pattern: PATTERN.numeric },
  card_text: { type: SCHEMA_TYPE.string, min: 1, max: 50 },
};
